import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { getSettings, getNavigation, resolveMedia } from "../../lib/api";
import { trackBooking } from "../../lib/tracking";
import { useLang, switchPath } from "../../lib/i18n";

const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_a2ad48e5-f113-4504-acb4-865147bb1512/artifacts/2j5z7p8b_PREVENTUM%20DIEZ%20POS.svg";

// Hard-coded navigation per user spec. Sub-items can later be made CMS-driven.
// Note: "Start" removed — clicking the logo navigates home.
const NAV = [
  {
    label: "Preventum Diez", en: "Preventum Diez", to: "/preventum-diez/", testid: "preventum",
    children: [
      { label: "Was ist Preventum Diez?", en: "What is Preventum Diez?", to: "/preventum-diez/" },
      { label: "Für wen ist Preventum Diez gedacht?", en: "Who is Preventum Diez for?", to: "/preventum-diez/fuer-wen/" },
      { label: "Prävention durch Klarheit", en: "Prevention through clarity", to: "/praevention/" },
      { label: "Bewegung, Gesundheit & Belastbarkeit", en: "Movement, health & resilience", to: "/sportmedizin-leistungssport/" },
    ],
  },
  {
    label: "Medizin & Untersuchungen", en: "Medicine & Examinations", to: "/medizin-und-untersuchungen/", testid: "medizin",
  },
  {
    label: "Wissen", en: "Knowledge", to: "/wissen/", testid: "wissen",
    children: [
      { label: "Wissenswertes", en: "Good to know", to: "/wissen/" },
      { label: "FAQ", en: "FAQ", to: "/faq/" },
      { label: "Glossar", en: "Glossary", to: "/glossar/" },
      { label: "News", en: "News", to: "/news/" },
      { label: "Sport in jedem Alter", en: "Sport at any age", to: "/wissen/sport-in-jedem-alter/" },
      { label: "Sport nach Herzinfarkt", en: "Sport after a heart attack", to: "/wissen/sport-nach-herzinfarkt/" },
      { label: "Was vor dem Sport untersucht werden sollte", en: "What to check before exercising", to: "/wissen/untersuchung-vor-sportbeginn/" },
    ],
  },
  {
    label: "Praxis & Kontakt", en: "Practice & Contact", to: "/praxis-und-kontakt/", testid: "praxis",
    children: [
      { label: "Über Preventum Diez", en: "About Preventum Diez", to: "/preventum-diez-fakten/" },
      { label: "Dr. Kirchner", en: "Dr. Kirchner", to: "/dr-kirchner/" },
      { label: "Ablauf", en: "How it works", to: "/ablauf/" },
      { label: "Standort Diez", en: "Location Diez", to: "/standort-diez/" },
      { label: "Kontakt", en: "Contact", to: "/kontakt/" },
      { label: "Termin anfragen", en: "Request appointment", to: "/kontakt/#termin" },
    ],
  },
];

const navLabel = (item, lang) => (lang === "en" ? item.en || item.label : item.label);

// ============ Language switcher ============
function LangSwitch({ className = "" }) {
  const { lang } = useLang();
  const location = useLocation();
  const other = lang === "en" ? "de" : "en";
  const target = switchPath(location.pathname, other) + location.hash;
  return (
    <Link
      to={target}
      data-testid="lang-switch"
      aria-label={lang === "en" ? "Auf Deutsch wechseln" : "Switch to English"}
      className={`inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[13px] font-semibold text-ink hover:text-navy transition-colors ${className}`}
    >
      <Globe className="h-4 w-4" aria-hidden />
      {other.toUpperCase()}
    </Link>
  );
}

// ============ Desktop Dropdown ============
function DropdownItem({ item }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();
  const { lang, lp } = useLang();
  const to = lp(item.to);
  const active = to === "/" || to === "/en" ? location.pathname === to : location.pathname.startsWith(to);
  const label = navLabel(item, lang);

  const onEnter = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const onLeave = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };

  if (!item.children) {
    return (
      <NavLink
        to={to}
        end={item.to === "/"}
        data-testid={`nav-${item.testid}`}
        className={({ isActive }) =>
          `px-3 py-2 rounded-lg text-[14px] font-medium whitespace-nowrap transition-colors ${
            isActive ? "text-brandred" : "text-ink hover:text-navy"
          }`
        }
      >
        {label}
      </NavLink>
    );
  }

  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        data-testid={`nav-${item.testid}`}
        className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[14px] font-medium whitespace-nowrap transition-colors ${
          active ? "text-brandred" : "text-ink hover:text-navy"
        }`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          data-testid={`dropdown-${item.testid}`}
          className="absolute left-1/2 -translate-x-1/2 top-full pt-2 w-[300px] z-50"
        >
          <div className="rounded-2xl bg-surface neu-card !shadow-xl !p-2 border border-outline/40">
            {item.children.map((c) => (
              <Link
                key={c.to}
                to={lp(c.to)}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="block px-4 py-2.5 rounded-lg text-[14px] text-ink hover:bg-surface-muted hover:text-navy-dark transition-colors"
              >
                {navLabel(c, lang)}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Mobile accordion item ============
function MobileItem({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  const { lang, lp } = useLang();
  const label = navLabel(item, lang);
  if (!item.children) {
    return (
      <NavLink
        to={lp(item.to)}
        end={item.to === "/"}
        onClick={onNavigate}
        data-testid={`mnav-${item.testid}`}
        className={({ isActive }) =>
          `block px-4 py-3 rounded-lg text-base font-medium ${isActive ? "text-brandred bg-surface-muted" : "text-ink hover:bg-surface-muted"}`
        }
      >
        {label}
      </NavLink>
    );
  }
  return (
    <div className="border-b border-outline/40 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`mnav-${item.testid}-toggle`}
        className="w-full flex items-center justify-between px-4 py-3 text-base font-semibold text-navy-dark"
      >
        <span>{label}</span>
        <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open && (
        <div className="pb-2 pl-2" data-testid={`mdropdown-${item.testid}`}>
          {/* Hub overview link first */}
          <Link
            to={lp(item.to)}
            onClick={onNavigate}
            className="block px-4 py-2 rounded-md text-[14px] font-semibold text-navy hover:bg-surface-muted"
          >
            ▸ {label} — {lang === "en" ? "Overview" : "Übersicht"}
          </Link>
          {item.children.map((c) => (
            <Link
              key={c.to}
              to={lp(c.to)}
              onClick={onNavigate}
              className="block px-4 py-2 rounded-md text-[14px] text-ink hover:bg-surface-muted"
            >
              {navLabel(c, lang)}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [settings, setSettings] = useState({});
  const [nav, setNav] = useState(NAV);
  const location = useLocation();
  const { lang, t, lp } = useLang();

  useEffect(() => { getSettings(lang).then(setSettings).catch(() => {}); }, [lang]);
  useEffect(() => {
    getNavigation().then((d) => {
      if (d?.items?.length) {
        setNav(d.items.map((h) => ({
          label: h.label_de, en: h.label_en, to: h.route, testid: h.testid || h.route,
          children: (h.children && h.children.length)
            ? h.children.map((c) => ({ label: c.label_de, en: c.label_en, to: c.route }))
            : undefined,
        })));
      }
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!settings?.favicon_url) return;
    const href = resolveMedia(settings.favicon_url);
    let link = document.querySelector("link[rel='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = href;
  }, [settings]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const logo = settings.logo_landscape_url || settings.logo_url || FALLBACK_LOGO;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "neu-header" : "bg-surface/85 backdrop-blur-md"}`}
    >
      <div className="container-x">
        {/* Single row: Logo + Nav + CTA */}
        <div className="flex items-center justify-between gap-4 py-3 md:py-4">
          {/* Logo (left) */}
          <Link to={lp("/")} className="block shrink-0" data-testid="logo-home-link">
            <img
              src={logo}
              alt="Preventum Diez"
              className="h-9 md:h-10 lg:h-11 w-auto max-w-[200px] sm:max-w-[240px] md:max-w-[300px] object-contain"
            />
          </Link>

          {/* Desktop nav (centered) */}
          <nav
            aria-label={t("mainNav")}
            className="hidden lg:flex items-center gap-1 flex-1 justify-center"
          >
            {nav.map((item) => <DropdownItem key={item.label} item={item} />)}
          </nav>

          {/* Desktop CTA + language */}
          <div className="hidden lg:flex items-center gap-1 shrink-0">
            <LangSwitch />
            {settings.doctolib_url ? (
              <a href={settings.doctolib_url} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking("header")} className="btn-primary !h-10 !px-4 !text-[13px]" data-testid="header-cta-termin">
                {t("requestAppointment")}
              </a>
            ) : (
              <Link to={lp("/kontakt/")} onClick={() => trackBooking("header")} className="btn-primary !h-10 !px-4 !text-[13px]" data-testid="header-cta-termin">
                {t("requestAppointment")}
              </Link>
            )}
          </div>

          {/* Mobile: language + hamburger */}
          <div className="lg:hidden flex items-center gap-1">
            <LangSwitch />
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="p-2 rounded-md text-navy hover:bg-surface-muted"
              aria-label={open ? t("closeMenu") : t("openMenu")}
              aria-expanded={open}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu (accordion) */}
        {open && (
          <div className="lg:hidden border-t border-outline/40 -mx-4 md:-mx-6 px-2 pb-4 bg-surface" data-testid="mobile-menu">
            <nav className="flex flex-col py-2" aria-label={t("mobileNavLabel")}>
              {nav.map((item) => <MobileItem key={item.label} item={item} onNavigate={() => setOpen(false)} />)}
            </nav>
            <div className="px-2 pt-3">
              {settings.doctolib_url ? (
                <a href={settings.doctolib_url} target="_blank" rel="noopener noreferrer" onClick={() => { trackBooking("mobile_menu"); setOpen(false); }} className="btn-primary w-full justify-center" data-testid="mobile-cta-termin">
                  {t("requestAppointment")}
                </a>
              ) : (
                <Link to={lp("/kontakt/")} onClick={() => { trackBooking("mobile_menu"); setOpen(false); }} className="btn-primary w-full justify-center" data-testid="mobile-cta-termin">
                  {t("requestAppointment")}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
